import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateIcon
} from '@mui/icons-material';
import projectService, { ProjectDocument } from '../../services/projectService';

interface DocumentPreviewProps {
  open: boolean;
  onClose: () => void;
  document: ProjectDocument | null;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  open,
  onClose,
  document: documentProp
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (open && documentProp) {
      loadPreview();
    } else {
      cleanup();
    }

    return cleanup;
  }, [open, documentProp]);

  const cleanup = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setZoom(1);
    setRotation(0);
    setError(null);
  };

  const loadPreview = async () => {
    if (!documentProp) return;

    setLoading(true);
    setError(null);

    try {
      const blob = await projectService.downloadProjectDocument(documentProp.id);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar la vista previa');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!documentProp) return;

    try {
      const blob = await projectService.downloadProjectDocument(documentProp.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const linkElement = document.createElement('a');
      linkElement.href = url;
      linkElement.download = documentProp.original_file_name;
      document.body.appendChild(linkElement);
      linkElement.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(linkElement);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al descargar el documento');
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const isImageFile = (mimeType: string) => {
    return mimeType.startsWith('image/');
  };

  const isPdfFile = (mimeType: string) => {
    return mimeType === 'application/pdf';
  };

  const isPreviewable = (mimeType: string) => {
    return isImageFile(mimeType) || isPdfFile(mimeType);
  };

  const getDocumentTypeLabel = (documentType: string) => {
    const labels: { [key: string]: string } = {
      brochure: 'Brochure',
      floor_plan: 'Plano',
      additional_info: 'Información Adicional',
      image: 'Imagen',
      video: 'Video',
      document: 'Documento'
    };
    return labels[documentType] || documentType;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!documentProp) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" component="div" noWrap>
              {documentProp.original_file_name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Chip
                label={getDocumentTypeLabel(documentProp.document_type)}
                size="small"
                color="primary"
              />
              <Typography variant="caption" color="text.secondary">
                {documentProp.project_code} • {formatFileSize(documentProp.file_size)}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        {previewUrl && isPreviewable(documentProp.mime_type) && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            p={1}
            borderBottom="1px solid"
            borderColor="divider"
          >
            <IconButton onClick={handleZoomOut} disabled={zoom <= 0.25}>
              <ZoomOutIcon />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton onClick={handleZoomIn} disabled={zoom >= 3}>
              <ZoomInIcon />
            </IconButton>
            {isImageFile(documentProp.mime_type) && (
              <IconButton onClick={handleRotate}>
                <RotateIcon />
              </IconButton>
            )}
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Box>
        )}

        {/* Preview Content */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflow="auto"
          p={2}
        >
          {loading && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Cargando vista previa...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ maxWidth: 400 }}>
              {error}
            </Alert>
          )}

          {previewUrl && !loading && !error && (
            <>
              {isImageFile(documentProp.mime_type) && (
                <img
                  src={previewUrl}
                  alt={documentProp.original_file_name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    transition: 'transform 0.2s ease',
                    objectFit: 'contain'
                  }}
                />
              )}

              {isPdfFile(documentProp.mime_type) && (
                <iframe
                  src={previewUrl}
                  title={documentProp.original_file_name}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                    transition: 'transform 0.2s ease'
                  }}
                />
              )}

              {!isPreviewable(documentProp.mime_type) && (
                <Box textAlign="center">
                  <Typography variant="h6" gutterBottom>
                    Vista previa no disponible
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Este tipo de archivo no se puede previsualizar en el navegador.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{ mt: 2 }}
                  >
                    Descargar archivo
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cerrar
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Descargar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreview;